import type { HTMLProps, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * AuroraBackground — animated layered-gradient backdrop that pans slowly
 * across the surface. Two background layers (a "stripe" gradient and the
 * aurora color gradient) compose with `mix-blend-difference` to produce
 * the shimmery aurora-borealis effect.
 *
 * Adapted from the public component:
 *   - Removed the outer `<main>` element (we already have a single `<main>`
 *     in EpisteckLanding; nesting `<main>` inside `<main>` is an HTML error
 *     and a11y violation). Component now renders a `<div>` wrapper.
 *   - Default container height is removed (was `h-[100vh]`); pass through
 *     the className to size the wrapper to whatever section needs it.
 *
 * Requires:
 *   - `aurora` keyframes + animation in tailwind.config.ts
 *   - The `addColorCssVars` plugin so Tailwind colors are available as
 *     `var(--blue-500)` etc.
 */
interface AuroraBackgroundProps extends HTMLProps<HTMLDivElement> {
  children: ReactNode;
  /**
   * If true (default), masks the aurora to a soft radial in the top-right.
   * Set false to apply aurora across the whole surface.
   */
  showRadialGradient?: boolean;
}

export function AuroraBackground({
  className,
  children,
  showRadialGradient = true,
  ...props
}: AuroraBackgroundProps) {
  return (
    <div
      className={cn(
        'relative flex flex-col items-center justify-center bg-zinc-50 dark:bg-zinc-900 text-slate-950 transition-colors',
        className,
      )}
      {...props}
    >
      <div className="absolute inset-0 overflow-hidden">
        <div
          className={cn(
            // The layered gradients. Using `var(--white)` etc. requires the
            // `addColorCssVars` plugin in tailwind.config.ts.
            `
            [--white-gradient:repeating-linear-gradient(100deg,var(--white)_0%,var(--white)_7%,var(--transparent)_10%,var(--transparent)_12%,var(--white)_16%)]
            [--dark-gradient:repeating-linear-gradient(100deg,var(--black)_0%,var(--black)_7%,var(--transparent)_10%,var(--transparent)_12%,var(--black)_16%)]
            [--aurora:repeating-linear-gradient(100deg,var(--blue-500)_10%,var(--indigo-300)_15%,var(--blue-300)_20%,var(--violet-200)_25%,var(--blue-400)_30%)]
            [background-image:var(--white-gradient),var(--aurora)]
            dark:[background-image:var(--dark-gradient),var(--aurora)]
            [background-size:300%,_200%]
            [background-position:50%_50%,50%_50%]
            filter blur-[10px] invert dark:invert-0
            after:content-[""] after:absolute after:inset-0 after:[background-image:var(--white-gradient),var(--aurora)]
            after:dark:[background-image:var(--dark-gradient),var(--aurora)]
            after:[background-size:200%,_100%]
            after:animate-aurora after:[background-attachment:fixed] after:mix-blend-difference
            pointer-events-none
            absolute -inset-[10px] opacity-50 will-change-transform`,

            showRadialGradient &&
              `[mask-image:radial-gradient(ellipse_at_100%_0%,black_10%,var(--transparent)_70%)]`,
          )}
        />
      </div>
      {children}
    </div>
  );
}
